import type { AiJobRow } from "./types";
import {
  normalizeObjetivoValue,
  normalizeTopicForFamily,
  normalizeTypeValue,
  parseRoteiroPayload,
} from "./parseRoteiro";

function getPrimaryResult(job: AiJobRow) {
  return Array.isArray(job.ai_job_results) ? job.ai_job_results[0] : job.ai_job_results;
}

export function getRoteiroNormalizedMetadata(job: AiJobRow) {
  return job.request_metadata?.normalized ?? null;
}

function looksLikeVariationDetails(details: string | null | undefined): boolean {
  if (!details) return false;
  return (
    details.includes("Contexto adicional do usuário para a nova variação:") ||
    details.includes("Versões anteriores:") ||
    details.includes("Crie uma nova variação mantendo o mesmo tema, objetivo e formato.")
  );
}

export function getRoteiroGenerationKind(job: AiJobRow): "base" | "variant" {
  const normalized = getRoteiroNormalizedMetadata(job);
  if (normalized?.generationKind === "variant") return "variant";
  if (looksLikeVariationDetails(normalized?.details)) return "variant";
  return "base";
}

export function getRoteiroRootJobId(job: AiJobRow): string {
  const normalized = getRoteiroNormalizedMetadata(job);
  return normalized?.rootJobId ?? job.id;
}

export function isTopLevelRoteiroJob(job: AiJobRow): boolean {
  return getRoteiroGenerationKind(job) !== "variant";
}

export function getRoteiroTypeAndObjective(job: AiJobRow) {
  const normalized = getRoteiroNormalizedMetadata(job);
  const result = getPrimaryResult(job);
  const script = typeof result?.analysis_json?.script === "string" ? result.analysis_json.script : null;
  const parsed = script ? parseRoteiroPayload(script) : null;

  return {
    typeValue:
      normalizeTypeValue(normalized?.type) ??
      parsed?.typeValue ??
      normalizeTypeValue(result?.analysis_json?.type) ??
      null,
    objetivoValue:
      normalizeObjetivoValue(normalized?.objective) ??
      parsed?.objetivoValue ??
      normalizeObjetivoValue(result?.analysis_json?.objective) ??
      null,
  };
}

export function getRoteiroFamilyJobs(history: AiJobRow[], currentJob: AiJobRow): AiJobRow[] {
  const rootJobId = getRoteiroRootJobId(currentJob);
  const currentResult = getPrimaryResult(currentJob);
  const familyKey = normalizeTopicForFamily(currentResult?.analysis_json?.topic ?? null);
  const { typeValue, objetivoValue } = getRoteiroTypeAndObjective(currentJob);

  const byRoot = history
    .filter((candidate) => getRoteiroRootJobId(candidate) === rootJobId)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  if (byRoot.length > 1) return byRoot;

  if (!familyKey) return [currentJob];

  return history
    .filter((candidate) => {
      const candidateResult = getPrimaryResult(candidate);
      const candidateTopic = candidateResult?.analysis_json?.topic ?? null;
      if (normalizeTopicForFamily(candidateTopic) !== familyKey) return false;
      const candidateMeta = getRoteiroTypeAndObjective(candidate);
      if (typeValue && candidateMeta.typeValue && candidateMeta.typeValue !== typeValue) return false;
      if (objetivoValue && candidateMeta.objetivoValue && candidateMeta.objetivoValue !== objetivoValue) {
        return false;
      }
      return true;
    })
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}
